import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import Tag from '@/components/base/Tag';
import TvCard from '@/components/base/TvCard';
import EmptyState from '@/components/base/EmptyState';
import { tvSeriesList } from '@/mocks/tvSeries';
import { tvComments } from '@/mocks/comments';
import { useFavorites } from '@/hooks/useFavorites';
import { useCopy } from '@/hooks/useCopy';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function TvDetail() {
  const { id } = useParams<{ id: string }>();
  const series = tvSeriesList.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { copied, copy } = useCopy();

  usePageMeta(
    series ? `${series.title} - 剧情介绍与演员资料 - 漂亮的姐姐` : '电视剧详情 - 漂亮的姐姐',
    series ? `${series.title}（${series.year}）剧情简介、主要演员与相关资料。${series.synopsis.slice(0, 60)}…` : undefined,
  );

  if (!series) {
    return (
      <div className="w-full max-w-page mx-auto px-4 md:px-6 py-10">
        <EmptyState
          title="未找到该电视剧"
          description="你访问的剧集可能已下架或链接有误，去看看其他热门剧集吧。"
          actionLabel="返回电视剧分类"
          icon="ri-emotion-sad-line"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/category/tv-series');
          }}
        />
      </div>
    );
  }

  const favorite = isFavorite(series.id);
  const related = tvSeriesList.filter((item) => item.id !== series.id && item.tags.some((t) => series.tags.includes(t))).slice(0, 4);
  const infoRows = [
    { label: '导演', value: series.director },
    { label: '主演', value: series.cast.join(' / ') },
    { label: '类型', value: series.tags.join(' · ') },
    { label: '地区', value: series.region },
    { label: '首播年份', value: String(series.year) },
    { label: '集数', value: `${series.episodes} 集` },
  ];

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6 pb-24 lg:pb-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电视剧分类', to: '/category/tv-series' }, { label: series.title }]} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-4">
          <div className="w-full max-w-xs mx-auto lg:max-w-none aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
            <img src={series.poster} alt={`《${series.title}》主海报`} title={`${series.title} 电视剧海报`} className="w-full h-full object-cover object-top" />
          </div>
          <div className="hidden lg:flex flex-col gap-3 mt-5">
            <button
              type="button"
              onClick={() => toggleFavorite(series.id)}
              className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                favorite
                  ? 'bg-accent-500 text-background-50 dark:text-foreground-950 hover:bg-accent-600'
                  : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
              }`}
            >
              <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
              {favorite ? '已收藏（点击取消）' : '收藏这部剧'}
            </button>
            <button
              type="button"
              onClick={() => copy(window.location.href)}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line text-accent-600' : 'ri-link'}></i>
              {copied ? '链接已复制' : '复制分享链接'}
            </button>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{series.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {series.tags.map((tag) => (
                  <Tag key={tag} label={tag} variant="primary" />
                ))}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-label whitespace-nowrap">
                  <i className="ri-star-fill"></i>
                  {series.rating.toFixed(1)} 分
                </span>
              </div>
            </div>
          </div>

          <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            {infoRows.map((row) => (
              <div key={row.label} className="flex gap-3 text-sm">
                <dt className="w-20 shrink-0 text-foreground-500 font-label">{row.label}</dt>
                <dd className="text-foreground-900">{row.value}</dd>
              </div>
            ))}
          </dl>

          <section className="mt-6">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">剧情简介</h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{series.synopsis}</p>
          </section>

          <section className="mt-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">演员表与角色</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {series.roles.map((role) => (
                <div key={role.actor} className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200">
                  <div className="w-14 h-14 shrink-0 rounded-full overflow-hidden bg-background-200">
                    <img src={role.avatar} alt={`演员 ${role.actor}`} title={`${role.actor} 角色介绍`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading text-sm font-bold text-foreground-950 whitespace-nowrap">{role.actor}</p>
                    <p className="text-xs text-foreground-600 truncate">{role.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {related.length > 0 ? (
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((item) => (
              <TvCard key={item.id} series={item} favorite={isFavorite(item.id)} onToggleFavorite={toggleFavorite} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-10">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-1.5 h-6 rounded-full bg-secondary-500"></span>
          <h2 className="font-heading text-xl font-bold text-foreground-950">观众评论</h2>
          <span className="text-sm text-foreground-500">({tvComments.length})</span>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-200">
          {tvComments.map((comment) => (
            <div key={comment.id} className="flex gap-3 p-4 md:p-5">
              <div className="w-10 h-10 shrink-0 rounded-full overflow-hidden bg-background-200">
                <img src={comment.avatar} alt={`${comment.user} 头像`} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-label text-sm font-medium text-foreground-900">{comment.user}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{comment.time}</span>
                </div>
                <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
              </div>
            </div>
          ))}
          <p className="p-4 text-xs text-foreground-500">
            <i className="ri-information-line mr-1"></i>
            评论区为静态演示内容，本站暂不提供用户登录与发言功能。
          </p>
        </div>
      </section>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link to="/category/tv-series" className="inline-flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line"></i>
          返回电视剧分类
        </Link>
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-label text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          回到首页
        </Link>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background-50/95 backdrop-blur border-t border-background-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => toggleFavorite(series.id)}
          className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
            favorite ? 'bg-accent-500 text-background-50 dark:text-foreground-950' : 'bg-primary-500 text-background-50 dark:text-foreground-950'
          }`}
        >
          <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
          {favorite ? '已收藏' : '收藏'}
        </button>
        <button
          type="button"
          onClick={() => copy(window.location.href)}
          className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-label cursor-pointer whitespace-nowrap"
        >
          <i className={copied ? 'ri-check-line text-accent-600' : 'ri-link'}></i>
          {copied ? '已复制' : '分享'}
        </button>
      </div>
    </div>
  );
}